<template>
  <div class="emp-card svr">
    <!-- Kopf: Kampagne wählen + Optionen. Eigene Klassen — die scoped
         .emp-card__head-Styles des Eltern-Panels greifen hier nicht. -->
    <div class="svr-head">
      <div class="svr-head__title">
        <v-icon small class="svr-head__icon">mdi-clipboard-text-outline</v-icon>
        Umfrage-Auswertung
      </div>
      <div class="svr-head__controls">
        <v-checkbox
          v-model="includeTests" label="Test-Antworten einbeziehen"
          dense hide-details class="mt-0" @change="data && load()"
        />
        <v-select
          v-model="campaignId" :items="campaignItems"
          dense outlined hide-details placeholder="Kampagne wählen"
          class="svr-head__select"
        />
        <v-btn
          color="#7b5cd6" dark depressed small
          :loading="loading" :disabled="!campaignId" @click="load"
        >Laden</v-btn>
      </div>
    </div>

    <div v-if="!data" class="emp-hint">
      Kampagne wählen und laden. Antworten laufen fortlaufend ein — zum Aktualisieren einfach erneut laden.
    </div>

    <template v-else>
      <div v-if="data.testCount && !data.includeTests" class="emp-hint mb-3">
        {{ data.testCount }} Test-Antwort(en) ausgeblendet — Häkchen „Test-Antworten einbeziehen“ setzen, um sie zu sehen.
      </div>

      <!-- ═══ KPI-Zeile ═══ -->
      <div class="svr-kpis">
        <div class="svr-kpi">
          <div class="svr-kpi__value">{{ data.total.responses.toLocaleString('de-DE') }}</div>
          <div class="svr-kpi__label">Antworten</div>
          <div v-if="responseRate" class="svr-kpi__sub">{{ responseRate }} % Rücklauf</div>
        </div>
        <div class="svr-kpi">
          <div class="svr-kpi__value">{{ data.coreDone.toLocaleString('de-DE') }}</div>
          <div class="svr-kpi__label">Kernteil fertig</div>
          <div class="svr-kpi__sub">{{ pctOf(data.coreDone, data.total.responses) }}</div>
        </div>
        <div class="svr-kpi">
          <div class="svr-kpi__value">{{ data.bonusDone.toLocaleString('de-DE') }}</div>
          <div class="svr-kpi__label">Bonusteil fertig</div>
          <div class="svr-kpi__sub">{{ pctOf(data.bonusDone, data.total.responses) }}</div>
        </div>
        <div class="svr-kpi" :class="{ 'svr-kpi--alert': data.total.q2.yes }">
          <div class="svr-kpi__value">
            <v-icon v-if="data.total.q2.yes" small color="#d03b3b" class="mr-1">mdi-alert-circle</v-icon>{{ data.total.q2.yes }}
          </div>
          <div class="svr-kpi__label">Fremd-Flyer gemeldet</div>
          <div class="svr-kpi__sub">{{ flyerRate(data.total) }} der Befragten</div>
        </div>
        <div class="svr-kpi svr-kpi--accent">
          <div class="svr-kpi__value">{{ data.total.q5.nps === null ? '—' : data.total.q5.nps }}</div>
          <div class="svr-kpi__label">NPS gesamt</div>
          <div class="svr-kpi__sub">{{ npsVerdict(data.total.q5.nps) }}</div>
        </div>
      </div>

      <!-- ═══ Fulfiller-Vergleich (Kern der Untersuchung) ═══ -->
      <div class="svr-section">
        <div class="svr-section__title">Nach Fulfiller <span class="svr-section__hint">aus den Bestelldaten · Doboxa = Kontrollgruppe</span></div>
        <table class="svr-tbl">
          <tr>
            <th>Gruppe</th><th class="num">Antworten</th><th class="num">Flyer „Ja“</th><th class="num">Flyer-Quote</th>
            <th class="num">Schäden</th><th class="num">Zufriedenheit ⌀</th><th class="num">Vorschau-Treue ⌀</th><th class="num">NPS</th>
          </tr>
          <tr v-for="g in groups" :key="g.key" :class="{ 'svr-tbl__hot': g.key === 'myPoster' }">
            <td>{{ g.label }}</td>
            <td class="num">{{ g.agg.responses }}</td>
            <td class="num">{{ g.agg.q2.yes }}</td>
            <td class="num"><strong>{{ flyerRate(g.agg) }}</strong></td>
            <td class="num">{{ (g.agg.q3.pack || 0) + (g.agg.q3.damaged || 0) }}</td>
            <td class="num">{{ g.agg.q1.avg === null ? '—' : g.agg.q1.avg }}</td>
            <td class="num">{{ g.agg.q4.avg === null ? '—' : g.agg.q4.avg }}</td>
            <td class="num">{{ g.agg.q5.nps === null ? '—' : g.agg.q5.nps }}</td>
          </tr>
        </table>
      </div>

      <!-- ═══ NPS im Detail ═══ -->
      <div class="svr-section">
        <div class="svr-section__title">
          Weiterempfehlung (NPS)
          <span class="svr-section__hint">Promotoren (9–10) − Kritiker (0–6), Skala −100 … +100 · unter 0 kritisch, 0–30 okay, 30–50 gut, über 50 exzellent</span>
        </div>
        <div class="svr-nps">
          <div class="svr-nps__score">
            <div class="svr-nps__number">{{ data.total.q5.nps === null ? '—' : data.total.q5.nps }}</div>
            <div class="svr-nps__verdict">{{ npsVerdict(data.total.q5.nps) }}</div>
          </div>
          <div class="svr-nps__charts">
            <!-- Gestapelter Anteils-Balken -->
            <div v-if="data.total.q5.n" class="svr-stack">
              <div
                v-for="seg in npsSegments" :key="seg.key"
                class="svr-stack__seg" :class="'svr-stack__seg--' + seg.key"
                :style="{ width: (100 * seg.n / data.total.q5.n) + '%' }"
                :title="seg.label + ': ' + seg.n + ' (' + Math.round(100 * seg.n / data.total.q5.n) + ' %)'"
              />
            </div>
            <div class="svr-legend">
              <span v-for="seg in npsSegments" :key="'l' + seg.key" class="svr-legend__item">
                <i class="svr-legend__dot" :class="'svr-stack__seg--' + seg.key" />
                {{ seg.label }} <strong>{{ seg.n }}</strong>
              </span>
            </div>
            <!-- 0–10-Histogramm, Säulen nach Zone gefärbt -->
            <div class="svr-hist svr-hist--nps">
              <div v-for="n in 11" :key="'h' + n" class="svr-hist__col" :title="(n - 1) + ': ' + q5Count(n - 1) + '×'">
                <div v-if="q5Count(n - 1) === q5Max && q5Max > 0" class="svr-hist__cap">{{ q5Count(n - 1) }}</div>
                <div
                  class="svr-hist__bar"
                  :class="'svr-stack__seg--' + npsZone(n - 1)"
                  :style="{ height: histHeight(q5Count(n - 1), q5Max) }"
                />
                <div class="svr-hist__tick">{{ n - 1 }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ═══ Sterne-Bewertungen ═══ -->
      <div class="svr-section">
        <div class="svr-section__title">Bewertungen <span class="svr-section__hint">Verteilung 1–5 Sterne</span></div>
        <div class="svr-stars-grid">
          <div v-for="sq in starQuestions" :key="sq.key" class="svr-starchart">
            <div class="svr-starchart__head">
              {{ sq.label }} <strong>⌀ {{ sq.agg.avg === null ? '—' : sq.agg.avg }}</strong>
            </div>
            <div class="svr-hist">
              <div v-for="n in 5" :key="n" class="svr-hist__col" :title="n + '★: ' + (sq.agg.dist[n] || 0) + '×'">
                <div v-if="sq.agg.dist[n]" class="svr-hist__cap">{{ sq.agg.dist[n] }}</div>
                <div class="svr-hist__bar svr-hist__bar--accent" :style="{ height: histHeight(sq.agg.dist[n] || 0, distMax(sq.agg.dist)) }" />
                <div class="svr-hist__tick">{{ n }}★</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ═══ Flyer-Meldungen ═══ -->
      <div v-if="data.flyerReports.length" class="svr-section">
        <div class="svr-section__title">Flyer-Meldungen <span class="svr-section__hint">chronologisch — zeigt das Zeitfenster der Beilagen</span></div>
        <table class="svr-tbl">
          <tr><th>Bestelldatum</th><th>Fulfiller</th><th>Anbieter lt. Kunde</th><th>E-Mail</th><th>Order</th></tr>
          <tr v-for="(f, i) in data.flyerReports" :key="'f' + i">
            <td>{{ fmtDate(f.lastOrderDate) }}</td>
            <td>{{ f.group }}</td>
            <td><strong>{{ f.who || '—' }}</strong></td>
            <td>{{ f.email }}</td>
            <td><a v-for="oid in f.orderIds" :key="oid" :href="'/order/' + oid" target="_blank" class="mr-2">öffnen</a></td>
          </tr>
        </table>
      </div>

      <!-- ═══ Schadens-Meldungen ═══ -->
      <div v-if="data.damageReports.length" class="svr-section">
        <div class="svr-section__title">Schadens-Meldungen</div>
        <table class="svr-tbl">
          <tr><th>Bestelldatum</th><th>Fulfiller</th><th>Art</th><th>E-Mail</th><th>Gelöst?</th><th>Order</th></tr>
          <tr v-for="(d, i) in data.damageReports" :key="'d' + i">
            <td>{{ fmtDate(d.lastOrderDate) }}</td>
            <td>{{ d.group }}</td>
            <td>{{ d.kind === 'damaged' ? 'Produkt beschädigt' : d.kind === 'pack' ? 'Verpackung beschädigt' : 'Sonstiges: ' + (d.text || '—') }}</td>
            <td>{{ d.email }}</td>
            <td :class="{ 'svr-bad': d.solved === false }">{{ d.solved === null ? '—' : d.solved ? 'Ja' : 'NEIN' }}</td>
            <td><a v-for="oid in d.orderIds" :key="oid" :href="'/order/' + oid" target="_blank" class="mr-2">öffnen</a></td>
          </tr>
        </table>
      </div>

      <!-- ═══ Bonus-Fragen als Balkenlisten ═══ -->
      <div class="svr-section">
        <div class="svr-section__title">Bonus-Fragen</div>
        <div class="svr-bars-grid">
          <div v-for="block in barBlocks" :key="block.title" class="svr-barlist">
            <div class="svr-barlist__title">{{ block.title }}</div>
            <div v-if="!block.rows.length" class="svr-barlist__empty">Noch keine Antworten</div>
            <div v-for="row in block.rows" :key="row.key" class="svr-barrow" :title="row.label + ': ' + row.n">
              <div class="svr-barrow__label">{{ row.label }}</div>
              <div class="svr-barrow__track">
                <div class="svr-barrow__fill" :style="{ width: (100 * row.n / block.max) + '%' }" />
              </div>
              <div class="svr-barrow__n">{{ row.n }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ═══ Freitexte ═══ -->
      <div v-for="ql in quoteLists" :key="ql.title" class="svr-section">
        <template v-if="ql.items.length">
          <div class="svr-section__title">{{ ql.title }} <span class="svr-section__hint">{{ ql.items.length }}</span></div>
          <div v-for="(q, i) in ql.items" :key="i" class="svr-quote">
            <span class="svr-quote__group">{{ q.group }}<template v-if="q.rating !== undefined && q.rating !== null"> · {{ q.rating }}{{ ql.unit }}</template></span>
            {{ q.text }}
            <a v-for="oid in q.orderIds" :key="oid" :href="'/order/' + oid" target="_blank" class="svr-quote__order">Order</a>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { httpsCallable } from "firebase/functions";
import { functions as fbFunctions } from "../../../fb";

const SHOP_LABELS = { AQ_DE: "DE", AQ_FR: "FR", AQ_ES: "ES", AQ_PL: "PL", OH_US: "OhMy" };
const GROUP_LABELS = {
  myPoster: "myPoster", doboxa: "Doboxa (Kontrolle)", picanova: "Picanova",
  mixed: "Gemischt", other: "Sonstige",
};
const KEY_LABELS = {
  nothing: "Nichts – alles da", sizes: "Mehr Größen", styles: "Mehr Stile / Motive",
  products: "Andere Produkte", frames: "Größere Rahmen-Auswahl",
  instagram_facebook: "Instagram / Facebook", google: "Google", tiktok: "TikTok",
  chatgpt: "ChatGPT", recommendation: "Empfehlung Freunde / Familie",
  self: "Für sich selbst", gift: "Geschenk",
  partner: "Partner / Partnerin", parent: "Mutter / Vater", child: "Kind",
  friend: "Freund / Freundin", grandparent: "Großeltern",
  birthday: "Geburtstag", wedding: "Hochzeit", christmas: "Weihnachten",
  anniversary: "Jahrestag", memorial: "Trauerfall / Andenken", moving: "Einzug",
  other: "Sonstiges",
};

export default {
  name: "SurveyResultsPanel",
  props: {
    campaigns: { type: Array, default: () => [] },
  },
  data() {
    return {
      campaignId: null,
      includeTests: false,
      loading: false,
      data: null,
    };
  },
  computed: {
    campaignItems() {
      return this.campaigns.map((c) => ({
        text: `${c.name} (${SHOP_LABELS[c.shopCode] || c.shopCode})`,
        value: c.id,
      }));
    },
    selectedCampaign() {
      return this.campaigns.find((c) => c.id === this.campaignId) || null;
    },
    // Rücklauf = Antworten / real versendete Mails der Kampagne
    responseRate() {
      const sent = this.selectedCampaign?.counters?.sent || 0;
      if (!sent || !this.data) return null;
      return ((100 * this.data.total.responses) / sent).toFixed(1).replace(".", ",");
    },
    groups() {
      if (!this.data) return [];
      return Object.entries(this.data.byGroup || {})
        .map(([key, agg]) => ({ key, label: GROUP_LABELS[key] || key, agg }))
        .filter((g) => g.agg.responses > 0);
    },
    npsSegments() {
      const q5 = this.data?.total.q5 || {};
      return [
        { key: "neg", label: "Kritiker (0–6)", n: q5.detractors || 0 },
        { key: "mid", label: "Passive (7–8)", n: q5.passives || 0 },
        { key: "pos", label: "Promotoren (9–10)", n: q5.promoters || 0 },
      ];
    },
    q5Max() {
      const dist = this.data?.total.q5.dist || {};
      return Math.max(0, ...Object.values(dist));
    },
    starQuestions() {
      const t = this.data?.total || {};
      return [
        { key: "q1", label: "Zufriedenheit", agg: t.q1 || { dist: {}, avg: null } },
        { key: "q4", label: "Vorschau-Treue", agg: t.q4 || { dist: {}, avg: null } },
        { key: "q10", label: "Bestellprozess", agg: t.q10 || { dist: {}, avg: null } },
      ];
    },
    barBlocks() {
      const t = this.data?.total || {};
      const block = (title, dist) => {
        const rows = Object.entries(dist || {})
          .map(([key, n]) => ({ key, label: this.keyLabel(key), n }))
          .sort((a, b) => b.n - a.n);
        return { title, rows, max: Math.max(1, ...rows.map((r) => r.n)) };
      };
      return [
        block("Aufmerksam geworden über", t.q6),
        block("Für sich / Geschenk", t.q7),
        block("Beschenkte Person", t.q7_for),
        block("Anlass", t.q7_occasion),
        block("Gewünscht (Mehrfachauswahl)", t.q8),
      ];
    },
    quoteLists() {
      const d = this.data || {};
      return [
        { title: "Vorschau-Abweichungen — was gestört hat", items: d.previewIssues || [], unit: "★" },
        { title: "Bestellprozess — wo es gehakt hat", items: d.orderingIssues || [], unit: "/5" },
        { title: "Freitext-Feedback", items: d.feedback || [], unit: "" },
      ];
    },
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const call = httpsCallable(fbFunctions, "surveyResults", { timeout: 120000 });
        const { data } = await call({ campaignId: this.campaignId, includeTests: this.includeTests });
        this.data = data;
      } catch (e) {
        alert("Auswertung fehlgeschlagen: " + (e.message || e));
      } finally {
        this.loading = false;
      }
    },
    keyLabel(key) {
      if (String(key).startsWith("other: ")) return "Sonstiges: " + String(key).slice(7);
      return KEY_LABELS[key] || key;
    },
    q5Count(n) {
      return (this.data?.total.q5.dist || {})[n] || 0;
    },
    npsZone(n) {
      return n >= 9 ? "pos" : n >= 7 ? "mid" : "neg";
    },
    npsVerdict(nps) {
      if (nps === null || nps === undefined) return "noch keine Daten";
      if (nps < 0) return "kritisch";
      if (nps < 30) return "okay";
      if (nps < 50) return "gut";
      return "exzellent";
    },
    histHeight(n, max) {
      if (!n || !max) return "2px";
      return Math.max(6, Math.round((72 * n) / max)) + "px";
    },
    distMax(dist) {
      return Math.max(0, ...Object.values(dist || {}));
    },
    flyerRate(agg) {
      const n = (agg.q2.yes || 0) + (agg.q2.no || 0) + (agg.q2.unsure || 0);
      if (!n) return "—";
      return `${(((agg.q2.yes || 0) / n) * 100).toFixed(1).replace(".", ",")} %`;
    },
    pctOf(part, whole) {
      if (!whole) return "";
      return Math.round((100 * part) / whole) + " %";
    },
    fmtDate(d) {
      if (!d) return "—";
      const s = String(d).slice(0, 10).split("-");
      return s.length === 3 ? `${s[2]}.${s[1]}.${s[0].slice(2)}` : String(d);
    },
  },
};
</script>

<style lang="scss" scoped>
/* Farb-Rollen (dataviz-validiert): Einzelserien = Marke, NPS = diverging
   Rot ↔ Grau ↔ Blau; Text trägt IMMER Text-Farben, nie Serienfarben. */
.svr {
  --viz-accent: #7b5cd6;
  --viz-accent-soft: #ede9fb;
  --viz-neg: #e34948;
  --viz-mid: #d8d5cf;
  --viz-pos: #2a78d6;
  --ink-1: #1c1b1a;
  --ink-2: rgba(0, 0, 0, 0.58);
  --ink-3: rgba(0, 0, 0, 0.42);
  --line: rgba(0, 0, 0, 0.08);
}

/* ── Kopfzeile: Titel links, Steuerung rechts, umbruchfähig ── */
.svr-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px 16px;
  margin-bottom: 14px;

  &__title {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    font-weight: 700;
    color: var(--ink-1);
    white-space: nowrap;
  }

  &__icon {
    color: var(--viz-accent) !important;
    margin-right: 6px;
  }

  &__controls {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px 12px;
  }

  &__select {
    max-width: 320px;
    min-width: 220px;
  }
}

/* ── KPI-Zeile ── */
.svr-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-bottom: 22px;
}

.svr-kpi {
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 12px 14px;

  &__value {
    font-size: 24px;
    font-weight: 700;
    color: var(--ink-1);
    line-height: 1.2;
  }

  &__label {
    font-size: 12px;
    font-weight: 600;
    color: var(--ink-2);
    margin-top: 2px;
  }

  &__sub {
    font-size: 11px;
    color: var(--ink-3);
    margin-top: 1px;
  }

  &--accent {
    background: var(--viz-accent-soft);
    border-color: transparent;
  }

  &--alert {
    border-color: rgba(208, 59, 59, 0.4);
  }
}

/* ── Sektionen ── */
.svr-section {
  margin-bottom: 26px;

  &__title {
    font-size: 13px;
    font-weight: 700;
    color: var(--ink-1);
    margin-bottom: 8px;
  }

  &__hint {
    font-size: 11px;
    font-weight: 400;
    color: var(--ink-3);
    margin-left: 6px;
  }
}

/* ── Tabellen ── */
.svr-tbl {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    font-weight: 600;
    color: var(--ink-2);
    padding: 6px 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    white-space: nowrap;

    &.num { text-align: right; }
  }

  td {
    padding: 7px 10px;
    border-bottom: 1px solid var(--line);
    color: var(--ink-1);

    &.num {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
  }

  &__hot td {
    background: #fdf6ee;
    font-weight: 600;
  }
}

.svr-bad {
  color: #b3261e;
  font-weight: 700;
}

/* ── NPS-Block ── */
.svr-nps {
  display: flex;
  gap: 26px;
  align-items: flex-start;

  &__score { min-width: 110px; text-align: center; padding-top: 8px; }

  &__number {
    font-size: 52px;
    font-weight: 700;
    color: var(--ink-1);
    line-height: 1;
  }

  &__verdict { font-size: 13px; color: var(--ink-2); margin-top: 4px; }

  &__charts { flex: 1; min-width: 0; }
}

/* gestapelter Anteils-Balken: 2px Lücken in Oberflächenfarbe */
.svr-stack {
  display: flex;
  gap: 2px;
  height: 18px;
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 8px;

  &__seg { min-width: 3px; }
  &__seg--neg { background: var(--viz-neg); }
  &__seg--mid { background: var(--viz-mid); }
  &__seg--pos { background: var(--viz-pos); }
}

.svr-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  font-size: 12px;
  color: var(--ink-2);
  margin-bottom: 14px;

  &__item { display: inline-flex; align-items: center; gap: 5px; }

  &__dot {
    width: 9px;
    height: 9px;
    border-radius: 3px;
    display: inline-block;
  }
}

/* ── Histogramme (Säulen: schmal, 4px runde Datenenden, eckige Basis) ── */
.svr-hist {
  display: flex;
  gap: 6px;
  align-items: flex-end;

  &__col {
    flex: 1;
    max-width: 42px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    min-height: 100px;
  }

  &__bar {
    width: 100%;
    max-width: 24px;
    border-radius: 4px 4px 0 0;
    background: var(--viz-mid);

    &--accent { background: var(--viz-accent); }
  }

  &__cap {
    font-size: 11px;
    color: var(--ink-2);
    margin-bottom: 2px;
    font-variant-numeric: tabular-nums;
  }

  &__tick {
    font-size: 11px;
    color: var(--ink-3);
    margin-top: 4px;
    border-top: 1px solid var(--line);
    width: 100%;
    text-align: center;
    padding-top: 3px;
  }
}

/* ── Sterne-Raster ── */
.svr-stars-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
}

.svr-starchart__head {
  font-size: 12px;
  color: var(--ink-2);
  margin-bottom: 6px;

  strong { color: var(--ink-1); }
}

/* ── horizontale Balkenlisten (Bonus-Fragen) ── */
.svr-bars-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 18px 28px;
}

.svr-barlist {
  &__title {
    font-size: 12px;
    font-weight: 600;
    color: var(--ink-2);
    margin-bottom: 6px;
  }

  &__empty { font-size: 12px; color: var(--ink-3); }
}

.svr-barrow {
  display: grid;
  grid-template-columns: minmax(110px, 40%) 1fr 30px;
  align-items: center;
  gap: 8px;
  margin-bottom: 5px;
  font-size: 12px;

  &__label {
    color: var(--ink-1);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__track { height: 12px; }

  &__fill {
    height: 100%;
    min-width: 3px;
    background: var(--viz-accent);
    border-radius: 0 4px 4px 0;
  }

  &__n {
    text-align: right;
    color: var(--ink-2);
    font-variant-numeric: tabular-nums;
  }
}

/* ── Freitext-Zitate ── */
.svr-quote {
  font-size: 13px;
  padding: 8px 12px;
  border-left: 3px solid #e0dcd4;
  background: #faf9f7;
  border-radius: 0 8px 8px 0;
  margin-bottom: 6px;
  color: var(--ink-1);

  &__group {
    display: inline-block;
    font-size: 11px;
    font-weight: 600;
    color: var(--viz-accent);
    margin-right: 6px;
    text-transform: uppercase;
  }

  &__order {
    font-size: 11px;
    font-weight: 600;
    margin-left: 8px;
    white-space: nowrap;
  }
}
</style>
